import type {
  LeadSourceDTO,
  MetaResponse,
  ProductDTO,
  RelationshipDTO,
  StageDTO,
  TagDTO,
  TerritoryDTO,
  TierDTO,
  UserSummaryDTO,
} from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../lib/api';

export const META_QUERY_KEY = ['meta'] as const;

/**
 * GET /api/meta — stages, relationships, tiers, users, tags, sources, products, settings… cached for 60 s.
 * Invalidate `META_QUERY_KEY` after changing any of these in Settings.
 */
export function useMeta() {
  return useQuery({
    queryKey: META_QUERY_KEY,
    queryFn: ({ signal }) => api.get<MetaResponse>('/api/meta', { signal }),
    staleTime: 60_000,
  });
}

export type Lookups = {
  stageById: (id: string | null | undefined) => StageDTO | undefined;
  stageByKey: (key: string | null | undefined) => StageDTO | undefined;
  relationshipById: (id: string | null | undefined) => RelationshipDTO | undefined;
  relationshipByKey: (key: string | null | undefined) => RelationshipDTO | undefined;
  tierByKey: (key: string | null | undefined) => TierDTO | undefined;
  /** Tier with the highest `minScore` ≤ score (same rule as the API). */
  tierForScore: (score: number | null | undefined) => TierDTO | undefined;
  userById: (id: string | null | undefined) => UserSummaryDTO | undefined;
  tagById: (id: string | null | undefined) => TagDTO | undefined;
  sourceById: (id: string | null | undefined) => LeadSourceDTO | undefined;
  territoryById: (id: string | null | undefined) => TerritoryDTO | undefined;
  productById: (id: string | null | undefined) => ProductDTO | undefined;
};

function indexBy<T>(items: readonly T[], key: (item: T) => string): (value: string | null | undefined) => T | undefined {
  const map = new Map(items.map((item) => [key(item), item]));
  return (value) => (value ? map.get(value) : undefined);
}

/** Pure lookup builder (exported for tests and non-hook callers). Empty lookups when meta is not loaded. */
export function buildLookups(meta: MetaResponse | undefined): Lookups {
  const tiersDesc = [...(meta?.tiers ?? [])].sort((a, b) => b.minScore - a.minScore);
  return {
    stageById: indexBy(meta?.stages ?? [], (s) => s.id),
    stageByKey: indexBy(meta?.stages ?? [], (s) => s.key),
    relationshipById: indexBy(meta?.relationships ?? [], (r) => r.id),
    relationshipByKey: indexBy(meta?.relationships ?? [], (r) => r.key),
    tierByKey: indexBy(meta?.tiers ?? [], (t) => t.key),
    tierForScore: (score) =>
      score === null || score === undefined ? undefined : tiersDesc.find((t) => t.minScore <= score),
    userById: indexBy(meta?.users ?? [], (u) => u.id),
    tagById: indexBy(meta?.tags ?? [], (t) => t.id),
    sourceById: indexBy(meta?.sources ?? [], (s) => s.id),
    territoryById: indexBy(meta?.territories ?? [], (t) => t.id),
    productById: indexBy(meta?.products ?? [], (p) => p.id),
  };
}

/** `const { meta, lookups } = useLookups(); lookups.stageById(lead.stageId)?.label` */
export function useLookups(): { meta: MetaResponse | undefined; lookups: Lookups; isLoading: boolean } {
  const { data: meta, isLoading } = useMeta();
  const lookups = useMemo(() => buildLookups(meta), [meta]);
  return { meta, lookups, isLoading };
}
